import { Alert, Box, Button, Card, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import { onSnapshot, Timestamp } from "firebase/firestore";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CloseGroup, DeleteClass, EditClass, GetAllGroupsInClass, GetAllStaffUsers, GetAllStudents, GetClassRef, GetUser, HasAccessToClass, ResetGroup, SetClassAccess } from "../FirebaseQueries";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Warning } from "@mui/icons-material";
import EditClassDialog from "../components/EditClassDialog";
import CloseIcon from "@mui/icons-material/Close";
import { Error } from "@mui/icons-material";
import CreateClassGroupDialog from "../components/CreateClassGroupDialog";
import ImportClassGroupsDialog from "../components/ImportClassGroupsDialog";
import { scenarioTitles } from "../Scenarios/Scenarios";
import { grey, red } from "@mui/material/colors";
import EditClassGroupDialog from "../components/EditClassGroupDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useAuth } from "../AuthProvider";
import { IClass } from "../Types";
import EditClassAccessDialog from "../components/EditClassAccessDialog";


interface IGroupData {
    id: string;
    isPlaying: boolean;
    groupNumber: number;
    assignedPlayers: string[];
}

interface User {
    id: string;
    email: string;
    fullName: string;
}

interface UserWithAccess {
    id: string;
    email: string;
    fullName: string;
    access: string;
}

interface Group {
    id: string;
    number: number;
}

export default function Class() {
    let params = useParams();
    const { currentUser } = useAuth();
    const [userCanViewClass, setUserCanViewClass] = useState<boolean | null>(null);
    const [classDeleted, setClassDeleted] = useState(false);
    const [classData, setClassData] = useState<IClass | undefined>();

    const userIsOwner: boolean = useMemo(() => {
        let userId = currentUser !== null ? currentUser.uid : "";
        return classData?.ownerId === userId;
    }, [classData]);

    const userCanEditClass: boolean | undefined = useMemo(() => {
        let userId = currentUser !== null ? currentUser.uid : "";
        return (classData?.ownerId === userId || classData?.editorIds.includes(userId) || classData?.canEditByLink)
    }, [classData]);

    const [codeEdit, setCodeEdit] = useState("");
    const [sessionEdit, setSessionEdit] = useState(1);
    const [scenarioIndexEdit, setScenarioIndexEdit] = useState(0);
    const [startTimeEdit, setStartTimeEdit] = useState(new Date());
    const [endTimeEdit, setEndTimeEdit] = useState(new Date());

    const [groupIdEdit, setGroupIdEdit] = useState("");
    const [groupToDelete, setGroupToDelete] = useState<Group | undefined>(undefined);
    const [groupToReset, setGroupToReset] = useState<Group | undefined>(undefined);
    const [groupNumberEdit, setGroupNumberEdit] = useState(0);
    const [assignedStudentsEdit, setAssignedStudentsEdit] = useState<string[]>([]);

    const [editorIdsEdit, setEditorIdsEdit] = useState<string[]>([]);
    const [viewerIdsEdit, setViewerIdsEdit] = useState<string[]>([]);
    const [canEditByLinkEdit, setCanEditByLinkEdit] = useState<boolean | undefined>();
    const [canViewByLinkEdit, setCanViewByLinkEdit] = useState<boolean | undefined>();

    const [allClassGroups, setAllClassGroups] = useState<IGroupData[]>([]);
    const [studentUsers, setStudentUsers] = useState<User[]>([]);
    const [staffUsers, setStaffUsers] = useState<User[]>([]);

    const [isEditClassDialogOpen, setIsEditClassDialogOpen] = React.useState(false);
    const [isEditClassAccessDialogOpen, setIsEditClassAccessDialogOpen] = React.useState(false);
    const [isDeleteClassDialogOpen, setIsDeleteClassDialogOpen] = React.useState(false);
    const [isEditGroupDialogOpen, setIsEditGroupDialogOpen] = React.useState(false);
    const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = React.useState(false);
    const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = React.useState(false);
    const [isResetGroupDialogOpen, setIsResetGroupDialogOpen] = React.useState(false);
    const [isImportCSVDialogOpen, setIsImportCSVDialogOpen] = React.useState(false);
    const [errorEditingClass, setErrorEditingClass] = React.useState(false);
    const [errorEditingClassAccess, setErrorEditingClassAccess] = React.useState(false);

    dayjs.extend(relativeTime);

    const dateFormatString = "HH:mm - D MMM, YYYY";

    const classId = () => {
        return params.classId !== undefined ? params.classId : "";
    };

    const userId: string = currentUser !== null ? currentUser.uid : "";

    const SessionStatus = {
        WAITING: -1,
        LIVE: 0,
        FINISHED: 1,
    };

    const username = (id: string) => {
        const staff = staffUsers.find((item) => item.id === id);
        return staff === undefined ? "User" : staff.fullName;
    }


    // ---------- On Mount ----------

    useEffect(() => { // Get Class
        (async () => {
            const canUserViewClass: boolean = await HasAccessToClass(classId(), userId);
            setUserCanViewClass(canUserViewClass);

            if (canUserViewClass) {
                const unsubscribe = onSnapshot(GetClassRef(classId()), (snapshot) => {
                    if (snapshot.exists()) {
                        let newClass: IClass = {
                            code: snapshot.data().code,
                            session: snapshot.data().session,
                            scenarioIndex: snapshot.data().scenarioIndex,
                            startTime: snapshot.data().startTime,
                            endTime: snapshot.data().endTime,
                            dateTimeCreated: snapshot.data().dateTimeCreated,
                            ownerId: snapshot.data().ownerId,
                            editorIds: snapshot.data().editorIds,
                            viewerIds: snapshot.data().viewerIds,
                            canEditByLink: snapshot.data().canEditByLink,
                            canViewByLink: snapshot.data().canViewByLink
                        };
                        setCodeEdit(snapshot.data().code);
                        setSessionEdit(snapshot.data().session);
                        setScenarioIndexEdit(snapshot.data().scenarioIndex);
                        setStartTimeEdit(snapshot.data().startTime.toDate());
                        setEndTimeEdit(snapshot.data().endTime.toDate());
                        setClassData(newClass);
                    } else {
                        setClassDeleted(true);
                    }
                });
                return unsubscribe;
            }
        })();
    }, []);

    useEffect(() => { // Get Groups
        if (userCanViewClass) {
            const classId = params.classId !== undefined ? params.classId : "";
            const q = GetAllGroupsInClass(classId);
            const unsubscribe = onSnapshot(q, (snapshot) => {
                if (!snapshot.empty) {
                    let newGroups: IGroupData[] = [];
                    snapshot.forEach((doc) => {
                        newGroups.push({
                            id: doc.id,
                            isPlaying: doc.data().isPlaying,
                            groupNumber: doc.data().groupNumber,
                            assignedPlayers: doc.data().assignedPlayers,
                        });
                    });
                    setAllClassGroups(newGroups);
                } else {
                    // Do something if empty
                }
            });
            return unsubscribe;
        }
    }, [userCanViewClass]);

    useEffect(() => { // Get Students
        if (userCanViewClass) {
            const GetStudents = async () => {
                try {
                    const studentsSnapshot = await GetAllStudents();
                    const value: User[] = [];
                    studentsSnapshot.forEach((doc) => {
                        value.push({
                            id: doc.id,
                            email: doc.data().email,
                            fullName: doc.data().fullName,
                        });
                    });

                    value.sort((a, b) => {
                        if (a.fullName < b.fullName) return -1;
                        if (a.fullName > b.fullName) return 1;
                        return 0;
                    });

                    setStudentUsers(value);
                } catch (err) {
                    // TODO
                    console.log(err);
                }
            };

            GetStudents();
        }
    }, [userCanViewClass]);

    useEffect(() => { // Get Staff Data 
        if (classData === undefined) return;

        const GetStaffData = async () => {
            try {
                const usersSnapshot = await GetAllStaffUsers();
                const value: User[] = [];

                usersSnapshot.forEach((doc) => {
                    value.push({
                        id: doc.id,
                        email: doc.data().email,
                        fullName: doc.data().fullName,
                    });
                });

                setStaffUsers(value);
            } catch (err) {
                // TODO
                console.log(err);
            }
        };

        GetStaffData();
    }, [classData]);

    // ---------- Functions ----------

    function studentById(id: string) {
        return studentUsers.find((student) => student.id === id);
    }

    function openEditDialog() {
        if (classData === undefined) return;

        setCodeEdit(classData.code);
        setSessionEdit(classData.session);
        setScenarioIndexEdit(classData.scenarioIndex);
        setStartTimeEdit(classData.startTime.toDate());
        setEndTimeEdit(classData.endTime.toDate());
        setIsEditClassDialogOpen(true);
    }

    function openEditAccessDialog() {
        if (classData === undefined) return;

        setEditorIdsEdit(classData.editorIds);
        setViewerIdsEdit(classData.viewerIds);
        setCanEditByLinkEdit(classData.canEditByLink);
        setCanViewByLinkEdit(classData.canViewByLink);
        setIsEditClassAccessDialogOpen(true);
    }

    function openEditGroupDialog(groupData: IGroupData) {
        setGroupIdEdit(groupData.id);
        setGroupNumberEdit(groupData.groupNumber);
        setAssignedStudentsEdit(groupData.assignedPlayers);
        setIsEditGroupDialogOpen(true);
    }

    function handleDeleteClass() {
        if (params.classId !== undefined) DeleteClass(params.classId);
        setIsDeleteClassDialogOpen(false);
    }

    function handleEditClass() {
        if (params.classId !== undefined) {
            try {
                EditClass(params.classId, codeEdit, sessionEdit, scenarioIndexEdit, Timestamp.fromDate(startTimeEdit), Timestamp.fromDate(endTimeEdit));
            } catch (err) {
                setErrorEditingClass(true);
            }
        }
        setIsEditClassDialogOpen(false);
    }

    function handleEditClassAccess(users: UserWithAccess[], canEditByLink: boolean, canViewByLink: boolean) {
        if (params.classId !== undefined) {
            try {
                let editorIds: string[] = [];
                let viewerIds: string[] = [];

                users.forEach((user) => {
                    if (user.access === "editor") editorIds.push(user.id);
                    if (user.access === "viewer") viewerIds.push(user.id);
                });

                SetClassAccess(params.classId, editorIds, viewerIds, canEditByLink, canViewByLink);
            } catch (err) {
                setErrorEditingClassAccess(true);
            }
        }
    }

    function handleCodeEdited(value: string) {
        setCodeEdit(value);
    }

    function handleSessionEdited(value: number) {
        setSessionEdit(value);
    }

    function handleScenarioIndexEdited(value: number) {
        setScenarioIndexEdit(value);
    }

    function handleStartTimeEdited(value: Date) {
        setStartTimeEdit(value);
    }

    function handleEndTimeEdited(value: Date) {
        setEndTimeEdit(value);
    }

    function getSessionStatus(): number {
        if (classData === undefined) return SessionStatus.WAITING;

        const timeNow = new Date();

        if (timeNow < classData.startTime.toDate()) {
            return SessionStatus.WAITING;
        } else if (timeNow > classData.startTime.toDate() && timeNow < classData.endTime.toDate()) {
            return SessionStatus.LIVE;
        } else {
            return SessionStatus.FINISHED;
        }
    }

    function setAndOpenDeleteGroupDialog(groupId: string, groupNumber: number) {
        setGroupToDelete({
            id: groupId,
            number: groupNumber,
        });
        setIsDeleteGroupDialogOpen(true);
    }

    function confirmDeleteGroup() {
        if (groupToDelete === undefined || groupToDelete.id === "") return;
        CloseGroup(groupToDelete.id);
        setIsDeleteGroupDialogOpen(false);
    }

    function setAndOpenResetGroupDialog(groupId: string, groupNumber: number) {
        setGroupToReset({
            id: groupId,
            number: groupNumber,
        });
        setIsResetGroupDialogOpen(true);
    }

    function confirmResetGroup() {
        if (groupToReset === undefined || groupToReset.id === "") return;
        ResetGroup(groupToReset.id);
        setIsResetGroupDialogOpen(false);
    }

    return (
        <>
            <Stack spacing={2}>
                {classData === undefined && userCanViewClass === null ?
                    <Card>
                        <CircularProgress aria-label="Loading…" />
                    </Card>
                    :
                    <>
                        {!userCanViewClass ?
                            <Card>
                                <Typography>You do not have permission to view this class.</Typography>
                            </Card>
                            :
                            <>
                                {classData === undefined ? <></> :
                                    <>
                                        <EditClassDialog
                                            isOpen={isEditClassDialogOpen}
                                            onClose={() => setIsEditClassDialogOpen(false)}
                                            code={codeEdit}
                                            session={sessionEdit}
                                            scenarioIndex={scenarioIndexEdit}
                                            startTime={startTimeEdit}
                                            endTime={endTimeEdit}
                                            onCodeChanged={handleCodeEdited}
                                            onSessionChanged={handleSessionEdited}
                                            onScenarioIndexChanged={handleScenarioIndexEdited}
                                            onStartTimeChanged={handleStartTimeEdited}
                                            onEndTimeChanged={handleEndTimeEdited}
                                            onEditSaved={handleEditClass}
                                        ></EditClassDialog>
                                        <EditClassAccessDialog
                                            isOpen={isEditClassAccessDialogOpen}
                                            users={staffUsers}
                                            ownerId={classData.ownerId}
                                            editorIds={editorIdsEdit}
                                            viewerIds={viewerIdsEdit}
                                            canEditByLink={classData.canEditByLink}
                                            canViewByLink={classData.canViewByLink}
                                            onClose={() => setIsEditClassAccessDialogOpen(false)}
                                            onEditSaved={handleEditClassAccess}
                                        >
                                        </EditClassAccessDialog>
                                        <EditClassGroupDialog
                                            isOpen={isEditGroupDialogOpen}
                                            onClose={() => setIsEditGroupDialogOpen(false)}
                                            groupId={groupIdEdit}
                                            groupNumber={groupNumberEdit}
                                            assignedStudents={assignedStudentsEdit}
                                            allStudents={studentUsers}
                                            otherGroups={allClassGroups.filter((group) => group.id !== groupIdEdit)}
                                        ></EditClassGroupDialog>
                                        <CreateClassGroupDialog
                                            isOpen={isCreateGroupDialogOpen}
                                            onClose={() => setIsCreateGroupDialogOpen(false)}
                                            classId={classId()}
                                            scenarioIndex={classData !== undefined ? classData.scenarioIndex : 0}
                                            allStudents={studentUsers}
                                            otherGroups={allClassGroups}
                                        ></CreateClassGroupDialog>
                                        <ImportClassGroupsDialog
                                            isOpen={isImportCSVDialogOpen}
                                            onClose={() => setIsImportCSVDialogOpen(false)}
                                            classId={classId()}
                                            scenarioIndex={classData !== undefined ? classData.scenarioIndex : 0}
                                            allStudents={studentUsers}
                                            otherGroups={allClassGroups}
                                        ></ImportClassGroupsDialog>
                                        <Collapse in={errorEditingClass} mountOnEnter unmountOnExit>
                                            <Alert
                                                icon={<Error fontSize="inherit" />}
                                                severity="error"
                                                action={
                                                    <IconButton
                                                        aria-label="close"
                                                        color="inherit"
                                                        size="small"
                                                        onClick={() => {
                                                            setErrorEditingClass(false);
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="inherit" />
                                                    </IconButton>
                                                }
                                            >
                                                There was an error editing the class. Please try again.
                                            </Alert>
                                        </Collapse>
                                        <Collapse in={errorEditingClassAccess} mountOnEnter unmountOnExit>
                                            <Alert
                                                icon={<Error fontSize="inherit" />}
                                                severity="error"
                                                action={
                                                    <IconButton
                                                        aria-label="close"
                                                        color="inherit"
                                                        size="small"
                                                        onClick={() => {
                                                            setErrorEditingClassAccess(false);
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="inherit" />
                                                    </IconButton>
                                                }
                                            >
                                                There was an error editing class access. Please try again.
                                            </Alert>
                                        </Collapse>
                                        <Collapse in={classDeleted}>
                                            <Alert
                                                icon={<Warning fontSize="inherit" />}
                                                severity="warning"
                                                action={
                                                    <Button variant="outlined" aria-label="close" color="inherit" size="small" href="/app/classes">
                                                        Go back to Classes
                                                    </Button>
                                                }
                                            >
                                                This class was deleted or does not exist.
                                            </Alert>
                                        </Collapse>
                                        {/* ---------- Delete Group Dialog ---------- */}
                                        <Dialog open={isDeleteGroupDialogOpen} onClose={() => setIsDeleteGroupDialogOpen(false)}>
                                            <DialogTitle>Delete Group {groupToDelete?.number}</DialogTitle>
                                            <DialogContent sx={{ minWidth: 400 }}>Are you sure? This cannot be undone.</DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setIsDeleteGroupDialogOpen(false)}>Cancel</Button>
                                                <Button onClick={confirmDeleteGroup} variant="contained" color="error">
                                                    Delete
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                        {/* ---------- Reset Group Dialog ---------- */}
                                        <Dialog open={isResetGroupDialogOpen} onClose={() => setIsResetGroupDialogOpen(false)}>
                                            <DialogTitle>Reset Group {groupToReset?.number}</DialogTitle>
                                            <DialogContent sx={{ minWidth: 400 }}>
                                                <Stack spacing={1}>
                                                    <Typography>This will remove all active and inactive players, and clear all answers.</Typography>
                                                    <Typography>Only do this if the group is stuck.</Typography>
                                                    <Typography>This cannot be undone.</Typography>
                                                </Stack>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setIsResetGroupDialogOpen(false)}>Cancel</Button>
                                                <Button onClick={confirmResetGroup} variant="contained" color="error">
                                                    Reset
                                                </Button>
                                            </DialogActions>
                                        </Dialog>
                                        {/* ---------- Main ---------- */}
                                        <Stack spacing={2}>
                                            <Grid container spacing={2}>
                                                {/* ---------- Information ---------- */}
                                                <Grid size={6}>
                                                    <Card sx={{ height: "100%", boxSizing: "border-box" }}>
                                                        <Stack direction={"row"} gap={1}>
                                                            <Stack justifyContent={"center"} alignItems={"start"} height={40} flexGrow={1} borderBottom={"1px solid"} borderColor={grey[300]}>
                                                                <Typography variant="subtitle1">Information</Typography>
                                                            </Stack>
                                                            <Stack direction={"row"} gap={1}>
                                                                {!userCanEditClass ? <></> :
                                                                    <Button onClick={openEditDialog} variant="contained" disabled={classDeleted}>
                                                                        Edit
                                                                    </Button>
                                                                }
                                                                {!userIsOwner ? <></> :
                                                                    <Button onClick={() => setIsDeleteClassDialogOpen(true)} disabled={classDeleted} variant="contained" color="error">
                                                                        Delete
                                                                    </Button>
                                                                }
                                                            </Stack>
                                                        </Stack>
                                                        <Stack spacing={0.5} marginTop={1}>
                                                            <Typography sx={{ textAlign: "left" }}>Class: {classData?.code}</Typography>
                                                            <Typography sx={{ textAlign: "left" }}>Session: {classData?.session}</Typography>
                                                            <Typography sx={{ textAlign: "left" }}>Scenario: {scenarioTitles[classData !== undefined ? classData.scenarioIndex : 0]}</Typography>
                                                            <Typography sx={{ textAlign: "left" }}>Start Time: {dayjs(classData?.startTime.toDate()).format(dateFormatString)}</Typography>
                                                            <Typography sx={{ textAlign: "left" }}>End Time: {dayjs(classData?.endTime.toDate()).format(dateFormatString)}</Typography>
                                                        </Stack>
                                                    </Card>
                                                </Grid>
                                                {/* ---------- Access ---------- */}
                                                <Grid size={3}>
                                                    <Card sx={{ height: "100%", boxSizing: "border-box" }}>
                                                        <Stack direction={"row"} alignItems={"center"} height={40} gap={1}>
                                                            <Stack justifyContent={"center"} alignItems={"start"} height={40} flexGrow={1} borderBottom={"1px solid"} borderColor={grey[300]}>
                                                                <Typography variant="subtitle1">
                                                                    Access
                                                                </Typography>
                                                            </Stack>
                                                            {!userCanEditClass ? <></> :
                                                                <Button onClick={openEditAccessDialog} variant="contained" disabled={classDeleted}>
                                                                    Edit
                                                                </Button>
                                                            }
                                                        </Stack>
                                                        <Stack spacing={1} marginTop={1} direction={"column"} justifyContent={"flex-start"}>
                                                            <Stack direction={"row"}>
                                                                <Typography textAlign={"left"} minWidth={64}>Public:</Typography>
                                                                <Typography textAlign={"left"}>
                                                                    {classData.canEditByLink ? "Can Edit" : classData.canViewByLink ? "Can View" : "None"}
                                                                </Typography>
                                                            </Stack>
                                                            <Stack direction={"row"}>
                                                                <Typography textAlign={"left"} minWidth={64}>Owner:</Typography>
                                                                <Typography textAlign={"left"}>{username(classData.ownerId)}</Typography>
                                                            </Stack>
                                                            <Stack direction={"row"}>
                                                                {/* ---------- Editors ---------- */}
                                                                <Typography textAlign={"left"} minWidth={64}>Editors:</Typography>
                                                                <Stack flexDirection={"column"} width={"100%"} maxHeight={"80px"} sx={{ overflowY: "auto" }}>
                                                                    {classData?.editorIds.length === 0 ?
                                                                        <Typography textAlign={"left"}>None</Typography>
                                                                        :
                                                                        <>
                                                                            {classData?.editorIds.map((id) => (
                                                                                <Typography key={id} textAlign={"left"}>{username(id)}</Typography>
                                                                            ))}
                                                                        </>
                                                                    }
                                                                </Stack>
                                                            </Stack>
                                                            <Stack direction={"row"}>
                                                                {/* ---------- Viewers ---------- */}
                                                                <Typography textAlign={"left"} minWidth={64}>Viewers:</Typography>
                                                                <Stack flexDirection={"column"} width={"100%"} maxHeight={"80px"} sx={{ overflowY: "auto" }}>
                                                                    {classData?.viewerIds.length === 0 ?
                                                                        <Typography textAlign={"left"}>None</Typography>
                                                                        :
                                                                        <>
                                                                            {classData?.viewerIds.map((id) => (
                                                                                <Typography key={id} textAlign={"left"}>{username(id)}</Typography>
                                                                            ))}
                                                                        </>
                                                                    }
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </Card>
                                                </Grid>
                                                {/* ---------- Status ---------- */}
                                                <Grid size={3}>
                                                    <Card sx={{ height: "100%", boxSizing: "border-box" }}>
                                                        <Stack justifyContent={"center"} alignItems={"start"} height={40} flexGrow={1} borderBottom={"1px solid"} borderColor={grey[300]}>
                                                            <Typography variant="subtitle1">
                                                                Status
                                                            </Typography>
                                                        </Stack>
                                                        <Stack spacing={1} marginTop={1}>
                                                            <Box sx={{ padding: 1, borderRadius: 1, border: "1px solid lightgrey" }}>
                                                                {getSessionStatus() === SessionStatus.WAITING ? <Typography fontWeight={"bold"}>Starts {dayjs(new Date()).to(classData?.startTime.toDate())}</Typography> : <></>}
                                                                {getSessionStatus() === SessionStatus.LIVE ? <Typography fontWeight={"bold"}>Live</Typography> : <></>}
                                                                {getSessionStatus() === SessionStatus.FINISHED ? <Typography fontWeight={"bold"}>Finished</Typography> : <></>}
                                                            </Box>
                                                            {getSessionStatus() === SessionStatus.WAITING ? (
                                                                <Button variant="contained" disabled>
                                                                    Waiting to start
                                                                </Button>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {getSessionStatus() === SessionStatus.LIVE ? (
                                                                <Button href={"/app/classes/progress/" + classId()} variant="contained">
                                                                    View Progress
                                                                </Button>
                                                            ) : (
                                                                <></>
                                                            )}
                                                            {getSessionStatus() === SessionStatus.FINISHED ? (
                                                                <Button href={`/app/classes/overview/${classId()}`} variant="contained">
                                                                    Results Overview
                                                                </Button>
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </Stack>
                                                    </Card>
                                                </Grid>
                                            </Grid>
                                            {/* ---------- Groups ---------- */}
                                            <Card>
                                                <Stack direction={"row"} spacing={2} sx={{ marginBottom: 1 }}>
                                                    <Stack direction={"row"} justifyContent={"space-between"} flexGrow={1} borderBottom={"1px solid"} borderColor={grey[300]}>
                                                        <Typography variant="subtitle1" textAlign={"left"}>
                                                            Groups
                                                        </Typography>
                                                        <Typography variant="subtitle1" textAlign={"left"}>
                                                            Total: {allClassGroups.length}
                                                        </Typography>
                                                    </Stack>
                                                    {!userCanEditClass ? <></> :
                                                        <Stack direction={"row"} spacing={1}>
                                                            <Button onClick={() => setIsImportCSVDialogOpen(true)} variant="contained" sx={{ minWidth: "130px" }}>
                                                                Import CSV
                                                            </Button>
                                                            <Button onClick={() => setIsCreateGroupDialogOpen(true)} variant="contained" sx={{ minWidth: "110px" }}>
                                                                Add Group
                                                            </Button>
                                                        </Stack>
                                                    }
                                                </Stack>
                                                {/* ---------- Groups ---------- */}
                                                {allClassGroups.length > 0 ? (
                                                    <Grid container spacing={1} marginTop={1}>
                                                        {allClassGroups
                                                            .sort((a, b) => a.groupNumber - b.groupNumber)
                                                            .map((group) => (
                                                                <Grid key={group.id} size={{ sm: 12, md: 6, lg: 4, xl: 3 }} justifyContent={"stretch"}>
                                                                    {/* ---------- Group ---------- */}
                                                                    <Card sx={{ backgroundColor: "white", padding: 0 }}>
                                                                        <Stack
                                                                            direction={"row"}
                                                                            justifyContent={"space-between"}
                                                                            alignItems={"center"}
                                                                            sx={{
                                                                                borderRadius: 1,
                                                                                padding: "0.75em 1em",
                                                                            }}
                                                                        >
                                                                            <Typography fontSize={24} fontWeight={"bold"}>
                                                                                <a href={`/app/groups/${group.id}`}>{group.groupNumber}</a>
                                                                            </Typography>
                                                                            {!userCanEditClass ? <></> :
                                                                                <Stack spacing={0.5} direction={"row"}>
                                                                                    {/* ---------- Edit Group ---------- */}
                                                                                    <Button onClick={() => openEditGroupDialog(group)} variant="contained">
                                                                                        Edit
                                                                                    </Button>
                                                                                    {/* ---------- Delete Group ---------- */}
                                                                                    <IconButton
                                                                                        aria-label="close"
                                                                                        color="inherit"
                                                                                        size="medium"
                                                                                        onClick={() => {
                                                                                            setAndOpenDeleteGroupDialog(group.id, group.groupNumber);
                                                                                        }}
                                                                                        sx={{
                                                                                            backgroundColor: red[50],
                                                                                            borderColor: red[100],
                                                                                            "&:hover": {
                                                                                                backgroundColor: red[100],
                                                                                                borderColor: red[200],
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <DeleteIcon fontSize="inherit" sx={{ color: red[300] }} />
                                                                                    </IconButton>
                                                                                </Stack>
                                                                            }
                                                                        </Stack>
                                                                        <Divider sx={{ margin: "0px 1em" }} />
                                                                        <Stack spacing={1} sx={{ padding: "1em", paddingTop: 0.5 }}>
                                                                            <Stack height={32} direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                                                                                <Typography variant="subtitle2">{group.isPlaying ? "Playing" : "Not Started"}</Typography>
                                                                                {!userCanEditClass ? <></> :
                                                                                    <IconButton
                                                                                        aria-label="close"
                                                                                        color="inherit"
                                                                                        size="medium"
                                                                                        onClick={() => {
                                                                                            setAndOpenResetGroupDialog(group.id, group.groupNumber);
                                                                                        }}
                                                                                        disabled={!group.isPlaying}
                                                                                    >
                                                                                        <RestartAltIcon fontSize="inherit" />
                                                                                    </IconButton>
                                                                                }
                                                                            </Stack>

                                                                            <Stack spacing={0.1} minWidth={"200px"}>
                                                                                {group.assignedPlayers.map((id, index) => (
                                                                                    <Stack key={index} direction={"row"} spacing={0.5}>
                                                                                        <Typography>{studentById(id)?.fullName}</Typography>
                                                                                        <Typography color={grey[600]}>({studentById(id)?.email})</Typography>
                                                                                    </Stack>
                                                                                ))}
                                                                            </Stack>
                                                                        </Stack>
                                                                    </Card>
                                                                </Grid>
                                                            ))}
                                                    </Grid>
                                                ) : (
                                                    <Typography variant="subtitle1" color="grey" sx={{ padding: 8 }}>
                                                        No Groups
                                                    </Typography>
                                                )}
                                            </Card>
                                        </Stack>
                                    </>
                                }
                            </>
                        }

                    </>
                }
            </Stack>
            <Dialog onClose={() => setIsDeleteClassDialogOpen(false)} open={isDeleteClassDialogOpen}>
                <DialogTitle>Delete Class</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure? This cannot be undone.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteClassDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteClass} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
